import type { IconName } from "@awesome.me/kit-01a9b9ee41/icons"
import { RotateProp } from "@fortawesome/fontawesome-svg-core"

import { ColorScheme } from "@particles/types"

import type { ObjectAvatarProps } from "@molecules/ObjectAvatar/ObjectAvatar.types"
import type { ObjectStatusBadgeProps } from "@molecules/ObjectStatusBadge/ObjectStatusBadge.types"

export type ObjectStatusStyle = {
    colorScheme: Exclude<ColorScheme, "black" | "white">
    icon: IconName
    iconRotation?: RotateProp
}

type ObjectStatusProps = ObjectStatusBadgeProps | ObjectAvatarProps

// One complete status → style record per object type that carries a status, keyed by the components' props unions
export type ObjectStatusStyles = {
    [Props in ObjectStatusProps as "status" extends keyof Props ? Props["objectType"] : never]: Record<
        Props extends { status?: infer Status } ? Exclude<Status, undefined> & string : never,
        ObjectStatusStyle
    >
}

// Shared looks, named for the state they signal across objects
const draft: ObjectStatusStyle = { colorScheme: "gray", icon: "circle-dashed" }
const unsent: ObjectStatusStyle = { colorScheme: "violet", icon: "circle-dashed" }
const inProgress: ObjectStatusStyle = { colorScheme: "blue", icon: "circle-half-stroke", iconRotation: 180 }
const overdue: ObjectStatusStyle = { colorScheme: "tomato", icon: "circle-exclamation" }
const completed: ObjectStatusStyle = { colorScheme: "orange", icon: "circle-check" }
const finalized: ObjectStatusStyle = { colorScheme: "jade", icon: "circle-check" }
const cancelled: ObjectStatusStyle = { colorScheme: "gray", icon: "circle-xmark" }
const active: ObjectStatusStyle = { colorScheme: "jade", icon: "circle-minus", iconRotation: 90 }
const inactive: ObjectStatusStyle = { colorScheme: "gray", icon: "ban" }
const onHold: ObjectStatusStyle = { colorScheme: "crimson", icon: "circle-stop" }

// Companies with on-hold sub-status groups colour internal holds differently; external holds look like a plain hold
const jobOnHoldStyles: Record<NonNullable<Job["status_color_override"]>, ObjectStatusStyle> = {
    "jobs-on-hold-external": onHold,
    "jobs-on-hold-internal": { colorScheme: "brown", icon: "circle-stop" },
}

// Shared by the pricebook item badge and the per-type pricebook avatars
const pricebookItem: ObjectStatusStyles["pricebook_item"] = {
    Active: active,
    Review: { colorScheme: "amber", icon: "clock" },
    Inactive: inactive,
}

export const objectStatusStyles: ObjectStatusStyles = {
    estimate: {
        Draft: draft,
        Unsent: unsent,
        "Awaiting Approval": inProgress,
        Expired: overdue,
        Unconverted: completed,
        Jobbed: finalized,
        Invoiced: finalized,
        Lost: cancelled,
        Cancelled: cancelled,
    },
    job: {
        Draft: draft,
        Unscheduled: unsent,
        Upcoming: inProgress,
        "Past Due": overdue,
        Active: { colorScheme: "jade", icon: "circle-play" },
        "Quick-Paused": { colorScheme: "amber", icon: "circle-pause" },
        "On Hold": onHold,
        Uninvoiced: completed,
        Unestimated: completed,
        Finalized: finalized,
        Cancelled: cancelled,
    },
    job_series: {
        Open: { colorScheme: "jade", icon: "circle-small" },
        Closed: { colorScheme: "gray", icon: "circle-small" },
    },
    invoice: {
        Draft: draft,
        Unsent: unsent,
        Outstanding: inProgress,
        Overdue: overdue,
        Paid: finalized,
        Voided: cancelled,
        Forgiven: cancelled,
    },
    credit_note: {
        Draft: draft,
        Unsent: unsent,
        Issued: finalized,
        Voided: cancelled,
    },
    purchase_order: {
        Draft: draft,
        Unsent: unsent,
        Sent: { colorScheme: "blue", icon: "circle-quarter-stroke", iconRotation: 90 },
        Acknowledged: { colorScheme: "jade", icon: "circle-quarter-stroke", iconRotation: 90 },
        "In Transit": { colorScheme: "cyan", icon: "circle-half-stroke", iconRotation: 180 },
        Unstocked: { colorScheme: "amber", icon: "circle-three-quarters-stroke", iconRotation: 270 },
        Unpaid: { colorScheme: "orange", icon: "circle-three-quarters-stroke", iconRotation: 270 },
        Paid: finalized,
        Cancelled: cancelled,
    },
    bill: {
        Draft: draft,
        Unsent: unsent,
        Outstanding: inProgress,
        Overdue: overdue,
        Paid: finalized,
        Voided: cancelled,
    },
    pricebook_item: pricebookItem,
    client: {
        Active: active,
        Inactive: inactive,
    },
    vendor: {
        Active: active,
        Inactive: inactive,
    },
    labor: pricebookItem,
    product: pricebookItem,
    other: pricebookItem,
    discount: pricebookItem,
    tax_rate: pricebookItem,
    warranty: {
        Active: finalized,
        Upcoming: { colorScheme: "blue", icon: "calendar-lines" },
        Expired: cancelled,
    },
    equipment: {
        Covered: { colorScheme: "jade", icon: "shield-halved" },
        "Partially Covered": { colorScheme: "amber", icon: "shield-exclamation" },
        Expired: { colorScheme: "gray", icon: "shield-xmark" },
    },
}

export function getObjectStatusStyle(
    objectType: keyof ObjectStatusStyles,
    status: string,
    statusColorOverride?: Job["status_color_override"],
): ObjectStatusStyle {
    if (objectType === "job" && statusColorOverride) {
        return jobOnHoldStyles[statusColorOverride]
    } else {
        // Each record is complete for its object's statuses; TS just can't correlate the two unions. A label
        // the map doesn't know yet (a new backend status, a stale search record) takes the draft look rather
        // than taking the page down
        return (objectStatusStyles[objectType] as Record<string, ObjectStatusStyle>)[status] ?? draft
    }
}
